/**
 * Feb 2026 — Review launch banner (Track B, Piece 2 of Option B comms).
 *
 * Renders a green-outlined nudge on /customer inviting the user to write
 * their first review for a delivered product they haven't yet reviewed.
 * The banner:
 *   - Is gated behind the `customer.reviews` feature flag (dark until launch).
 *   - Only appears when GET /api/customer/reviewable-products returns ≥1 item.
 *   - Is dismissible for 7 days per customer via localStorage.
 *   - Deep-links via `onOpen(productId)` — the parent dashboard component
 *     wires this to its own product-drawer open helper so we don't duplicate
 *     the drawer state model here.
 *
 * Kept as its own component so CustomerDashboard's mounting story stays
 * a one-liner: <ReviewLaunchBanner onOpen={handleOpenProduct} />.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, X, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFlag } from '../../context/FeatureFlagsContext';
import { useReviewRewards, reviewRewardHeadline } from '../../hooks/useReviewRewards';
import { daysLeftText } from '../../hooks/useReviewWindow';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;  // 7 days

function storageKey(userId) {
  return `review_launch_banner_dismissed_at:${userId || 'anon'}`;
}

function isDismissed(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch { return false; }
}

export function ReviewLaunchBanner({ onOpen, modern = false }) {
  const { user } = useAuth();
  const reviewsEnabled = useFlag('customer.reviews', false);
  const rewards = useReviewRewards(reviewsEnabled && user?.role === 'customer');
  const [candidates, setCandidates] = useState([]);
  const [dismissed, setDismissed] = useState(() => isDismissed(user?.id));
  const [loaded, setLoaded] = useState(false);

  // Prefetch reviewable products even when the flag is OFF, so the banner
  // appears instantly the moment the flag flips ON (no fetch latency).
  useEffect(() => {
    if (!user || user.role !== 'customer') { setLoaded(true); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/customer/reviewable-products`,
          { withCredentials: true },
        );
        if (alive) setCandidates(Array.isArray(data.products) ? data.products : []);
      } catch { /* silent — banner just won't show */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [user]);

  const handleDismiss = useCallback(() => {
    try { localStorage.setItem(storageKey(user?.id), String(Date.now())); }
    catch { /* private mode — dismiss for this session only */ }
    setDismissed(true);
  }, [user]);

  const handleOpen = useCallback(() => {
    const first = candidates[0];
    if (first && onOpen) onOpen(first.product_id);
  }, [candidates, onOpen]);

  if (!loaded || !reviewsEnabled || dismissed) return null;
  if (!candidates.length) return null;

  if (!rewards) return null;

  const first = candidates[0];
  const extraCount = candidates.length - 1;
  const headline = reviewRewardHeadline(rewards);
  const likeLine = rewards.likePts > 0 ? `, plus ${rewards.likePts} pts every time someone likes your review` : '';

  if (modern) {
    return (
      <ModernReviewBanner first={first} extraCount={extraCount} headline={headline} likeLine={likeLine}
        onDismiss={handleDismiss} onOpen={handleOpen} />
    );
  }

  return (
    <div
      className="relative bg-gradient-to-r from-[#0C0612] via-[#0F1C0A] to-[#0C0612] border border-[#39FF14] p-4 mb-4"
      data-testid="review-launch-banner"
    >
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-[#A3C7A8] hover:text-[#39FF14] transition-colors"
        data-testid="review-launch-banner-dismiss"
        aria-label="Dismiss for 7 days"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="mt-0.5 shrink-0">
          <Sparkles className="w-5 h-5 text-[#39FF14]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#39FF14] font-mono text-sm font-bold uppercase tracking-wider">
            {headline}
          </p>
          <p className="text-[#A3C7A8] font-mono text-xs mt-1 leading-relaxed">
            Share your take on <span className="text-[#39FF14] font-bold">{first.product_name}</span>
            {first.days_left != null && <span className={first.days_left <= 3 ? 'text-[#FF9F0A]' : ''} data-testid="review-launch-banner-days"> ({daysLeftText(first.days_left)})</span>}
            {extraCount > 0 && (
              <span> or {extraCount} other product{extraCount === 1 ? '' : 's'} you&apos;ve tried</span>
            )}
            . Approved reviews earn you points immediately{likeLine}.
          </p>
          <button
            type="button"
            onClick={handleOpen}
            className="mt-3 inline-flex items-center gap-1.5 bg-[#39FF14] text-[#050003] font-mono text-xs font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#4AF626]"
            data-testid="review-launch-banner-cta"
          >
            <Star className="w-3 h-3" />
            Write a review
          </button>
        </div>
      </div>
    </div>
  );
}

function ModernReviewBanner({ first, extraCount, headline, likeLine, onDismiss, onOpen }) {
  return (
    <div className="am-card" style={{ position: 'relative', padding: '16px 44px 16px 18px', marginBottom: 24, display: 'flex', gap: 14, alignItems: 'flex-start', border: '1px solid var(--am-hair)' }}
         data-testid="review-launch-banner">
      <button type="button" onClick={onDismiss} aria-label="Dismiss for 7 days" data-testid="review-launch-banner-dismiss"
        style={{ position: 'absolute', top: 12, right: 12, background: 'transparent', border: 0, color: 'var(--am-ink-3)', cursor: 'pointer' }}>
        <X size={16} />
      </button>
      <div style={{ width: 36, height: 36, borderRadius: 999, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--am-emerald-50, #ECFDF5)', color: 'var(--am-emerald-700)' }}>
        <Sparkles size={18} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: 'var(--am-ink-1)' }} data-testid="review-launch-banner-headline">{headline}</p>
        <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.5, color: 'var(--am-ink-2)' }}>
          Share your take on <strong style={{ color: 'var(--am-ink-1)' }}>{first.product_name}</strong>
          {first.days_left != null && <span style={{ color: first.days_left <= 3 ? '#B45309' : undefined }} data-testid="review-launch-banner-days"> ({daysLeftText(first.days_left)})</span>}
          {extraCount > 0 && <span> or {extraCount} other product{extraCount === 1 ? '' : 's'} you&apos;ve tried</span>}
          . Approved reviews earn you points immediately{likeLine}.
        </p>
        <button type="button" onClick={onOpen} data-testid="review-launch-banner-cta"
          style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 999, border: 0, cursor: 'pointer', background: 'var(--am-emerald-700)', color: '#fff' }}>
          <Star size={13} /> Write a review
        </button>
      </div>
    </div>
  );
}

export default ReviewLaunchBanner;
